import React, { ReactNode, useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { TypeAnimation } from 'react-type-animation';

import { User } from 'src/interfaces/iuser';
import { callApi } from 'src/core/callApi';

import { Box, Typography, Divider, Grid } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  convertCurrency,
  formatCurrency,
  formatMonthString,
  formatDateString,
  getWeekdayFromDate,
} from 'src/utils/functions';
import { exchangeRates, SAMPLE_LISTING_ID, SCALE_CTA } from 'src/utils/const';
import { resolveDefinition } from 'src/utils/kpiDefinitions';
import CalendarHeatmap from 'src/App/Dashboards/Market/Components/DailyOccupancyRateCalendar/CalendarHeatmap';
import styles from './style';
import TypingLoader from 'src/App/Dashboards/Market/Components/Loaders/TypingLoader';
import RateLimitAlert from '../../Components/Alerts/RateLimitAlert';

interface PerformanceExplanationsSectionProps {
  listingID: string;
  handleOnSampleClick: (plan: string) => void;
  currency: string;
  setMarketUrlBack: () => void;
  triggerModalClose: any;
  isDemo: boolean;
  isDisabled: boolean;
  marketID?: string;
}

interface ExplanationData {
  performance_summary: {
    booked_days_count_ltm: number | null;
    active_days_count_ltm: number | null;
    occupancy_rate_ltm: number | null;
    raw_occupancy_rate_ltm?: number | null;
    confirmed_booked_days_ltm?: number | null;
    ltm_months_bounds: {
      start_month: string;
      end_month: string;
    };
    average_daily_rate_ltm: number | null;
    revenue_potential: number | null;
  };
  data_confidence_summary: {
    confidence_level: 'High' | 'Average' | 'Low' | null;
    checks: Array<{
      premise: string;
      is_ok: boolean;
    }>;
  };
  rate_trends: {
    observation_count_last_3_months: number | null;
    observation_count_breakdown: {
      weekdays: number | null;
      weekends: number | null;
    };
    recent_examples: Array<{
      start_date: string;
      end_date: string;
      rate: number;
      captured_days_ago: number;
    }>;
  };
  occupancy_trends: {
    monthly_observations: Array<{
      month: string;
      active_days_count?: number;
      booked_days_count?: number;
      raw_occupancy_rate: number;
      adjusted_occupancy_rate: number;
      confirmed_booked_days_count: number;
      last_minute_booked_days_count: number;
    }>;
  };
  latest_calendar_view: {
    combined_calendar_snapshot: Record<string, 'available' | 'booked'>;
    number_of_observation_points: number;
    active_days_count?: number;
  };
  mid_term_unavailable_months: Array<{
    month: string;
    blocked_days_count: number;
  }>;
}

/** TEMPORARY: hide in UI only; backend still uses this signal for confidence_level. */
const LISTING_LINK_ACTIVE_PREMISE =
  'The listing link has been active within the last month.';

interface AnimatedRevealProps {
  children: ReactNode;
  typingSpeed?: number;
  delay?: number;
}

const AnimatedReveal: React.FC<AnimatedRevealProps> = ({
  children,
  typingSpeed = 10,
  delay = 0,
}) => {
  const [textContent, setTextContent] = useState('');
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Extract text content from React children
    const extractText = (node: ReactNode): string => {
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return String(node);
      if (React.isValidElement(node)) {
        if (node.props.children) {
          return React.Children.toArray(node.props.children)
            .map(extractText)
            .join('');
        }
      }
      return '';
    };

    const fullText = extractText(children);
    setTextContent(fullText);

    const timeout = setTimeout(() => {
      setIsReady(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, [children, delay]);

  if (!isReady || !textContent.trim()) {
    return <>{children}</>;
  }

  return (
    <TypeAnimation
      sequence={[textContent]}
      wrapper="div"
      speed={{ type: 'keyStrokeDelayInMs', value: typingSpeed }}
      style={{ whiteSpace: 'pre-wrap' }}
    />
  );
};

const Template = ({
  premiumData,
  currency,
  isUserAllowed,
  isDemo,
  isLtmDataDisabled,
  setMarketUrlBack,
  triggerModalClose,
  handleOnSampleClick,
}: {
  premiumData: ExplanationData | null;
  currency: string;
  isUserAllowed: boolean;
  isDemo: boolean;
  isLtmDataDisabled: boolean;
  setMarketUrlBack: () => void;
  triggerModalClose: () => void;
  handleOnSampleClick: (plan: string) => void;
}) => {
  const data = premiumData;
  const [revealedBlocks, setRevealedBlocks] = useState(0);
  const [activeBlockIndex, setActiveBlockIndex] = useState<number | null>(null);
  const animationCompletedRef = useRef(false);
  const dataRef = useRef<any>(null);

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const defaultTypingDuration = 1400;
  const blockPauseDuration = 250;

  if (!data) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No performance data available
        </Typography>
      </Box>
    );
  }

  const performanceSummary = data?.performance_summary;
  const dataConfidenceSummary = data?.data_confidence_summary;
  const rateTrends = data?.rate_trends;
  const occupancyTrends = data?.occupancy_trends;
  const latestCalendarView = data?.latest_calendar_view;
  const midTermUnavailableMonths = data?.mid_term_unavailable_months;

  const bookedNights = performanceSummary?.booked_days_count_ltm || 0;
  const confirmedBookedNights = performanceSummary?.confirmed_booked_days_ltm ?? null;
  const totalNights = performanceSummary?.active_days_count_ltm || 0;
  const occupancyRate = performanceSummary?.occupancy_rate_ltm || 0;
  const rawOccupancyRate = performanceSummary?.raw_occupancy_rate_ltm ?? null;
  const startMonth = performanceSummary?.ltm_months_bounds?.start_month || '';
  const endMonth = performanceSummary?.ltm_months_bounds?.end_month || '';
  const averageDailyRate = performanceSummary?.average_daily_rate_ltm || 0;
  const annualRevenue = performanceSummary?.revenue_potential || 0;

  const useRangeFormat =
    confirmedBookedNights != null && rawOccupancyRate != null && totalNights > 0;

  const showScaleOnlyData = isUserAllowed || isDemo;
  const customTextStyleScaleOnly = useMemo(
    () =>
      !showScaleOnlyData
        ? {
            filter: 'blur(4px)',
          }
        : {},
    [showScaleOnlyData]
  );

  const CustomTextNonScale = ({ children }: { children: ReactNode }) => (
    <>
      {isLtmDataDisabled && !isDemo ? (
        <span
          onClick={() => {
            setMarketUrlBack();
            triggerModalClose();
            handleOnSampleClick('expert');
          }}
          style={{ filter: 'blur(4px)', cursor: 'pointer' }}
        >
          XXXX
        </span>
      ) : (
        children
      )}
    </>
  );

  const typingPlaceholder = ({
    marginBottom = '24px',
    message,
    includeDivider = false,
  }: {
    marginBottom?: string | number;
    message: string;
    includeDivider?: boolean;
  }) => (
    <>
      <Box
        sx={{
          marginBottom,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <TypingLoader size={10} color="#000" />
        <Typography variant="body2" color="text.secondary">
          <AnimatedReveal>{message}</AnimatedReveal>
        </Typography>
      </Box>
      {includeDivider ? <Divider sx={{ marginBottom: '32px' }} /> : null}
    </>
  );

  type TypingBlock = {
    key: string;
    content: ReactNode;
    placeholder: ReactNode;
    typingDuration?: number;
  };

  const blocks: TypingBlock[] = useMemo(
    () => [
      {
        key: 'summary',
        content: (
          <>
            <Box sx={{ marginBottom: '32px' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '12px' }}>
                Listing Performance Summary
              </Typography>
              <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                This listing was booked for{' '}
                {useRangeFormat ? (
                  <>
                    <CustomTextNonScale>{confirmedBookedNights}–{bookedNights}</CustomTextNonScale>{' '}
                    nights out of <CustomTextNonScale>{totalNights}</CustomTextNonScale> active
                    nights (≈ <CustomTextNonScale>{rawOccupancyRate}–{occupancyRate}%</CustomTextNonScale>{' '}
                    occupancy)
                  </>
                ) : (
                  <>
                    <CustomTextNonScale>{bookedNights}</CustomTextNonScale> nights out of{' '}
                    <CustomTextNonScale>{totalNights}</CustomTextNonScale> nights (≈{' '}
                    <CustomTextNonScale>{occupancyRate}%</CustomTextNonScale> occupancy)
                  </>
                )}{' '}
                between {formatMonthString(startMonth)} – {formatMonthString(endMonth)}, with an
                average booked daily rate of {exchangeRates[currency].symbol}
                {formatCurrency(String(convertCurrency(currency, averageDailyRate)))}.
              </Typography>
              <Typography variant="body1">
                If the listing had been available for the entire year, it could have generated an
                estimated{' '}
                <strong>
                  {exchangeRates[currency].symbol}
                  <CustomTextNonScale>
                    {formatCurrency(String(convertCurrency(currency, annualRevenue)))}
                  </CustomTextNonScale>{' '}
                  annual revenue
                </strong>
                .
              </Typography>
            </Box>

            <Divider sx={{ marginBottom: '32px' }} />
          </>
        ),
        placeholder: typingPlaceholder({
          marginBottom: '32px',
          message: 'Summarizing performance metrics...',
          includeDivider: true,
        }),
        typingDuration: 1800,
      },
      {
        key: 'confidence',
        content: (
          <>
            <Box sx={{ marginBottom: '32px' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '12px' }}>
                Data Confidence Level
              </Typography>
              <Typography variant="body1" sx={{ marginBottom: '16px' }}>
                <strong>
                  Airbtics Confidence: {dataConfidenceSummary?.confidence_level || 'Unknown'}
                </strong>
              </Typography>
              <Typography variant="body2" sx={{ marginBottom: '16px' }}>
                Based on the following signals:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {dataConfidenceSummary?.checks
                  ?.filter((check) => check.premise !== LISTING_LINK_ACTIVE_PREMISE)
                  ?.map((check, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {check.is_ok ? (
                      <CheckCircleIcon sx={{ color: 'green', fontSize: '20px' }} />
                    ) : (
                      <CancelIcon sx={{ color: 'red', fontSize: '20px' }} />
                    )}
                    <Typography variant="body2">{check.premise}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Divider sx={{ marginBottom: '32px' }} />
          </>
        ),
        placeholder: typingPlaceholder({
          marginBottom: '32px',
          message: 'Checking confidence signals...',
          includeDivider: true,
        }),
        typingDuration: 1400,
      },
      {
        key: 'rate-trends',
        content: (
          <Box sx={{ marginBottom: '24px' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '12px' }}>
              Advanced Insights <em style={{ fontSize: '0.9em', color: '#666' }}>(Scale Only)</em>
            </Typography>

            <Box sx={{ marginBottom: '24px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '8px' }}>
                Rate Trends
              </Typography>
              <Typography variant="body2" sx={{ marginBottom: '12px' }}>
                {resolveDefinition('ADR_listing_LTM', {
                  _template: 'rateTrendsTemplate',
                  currency: exchangeRates[currency].symbol,
                  amount: formatCurrency(String(convertCurrency(currency, averageDailyRate))),
                  observationCount: String(rateTrends?.observation_count_last_3_months || 0),
                  weekdays: String(rateTrends?.observation_count_breakdown?.weekdays || 0),
                  weekends: String(rateTrends?.observation_count_breakdown?.weekends || 0),
                })}
              </Typography>
              {rateTrends?.recent_examples && rateTrends?.recent_examples?.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ marginBottom: '8px' }}>
                    Recent examples:
                  </Typography>
                  <Box
                    sx={{
                      marginLeft: '16px',
                      position: 'relative',
                    }}
                  >
                    <ul
                      style={{
                        paddingLeft: 20,
                        marginBlockStart: 0,
                        marginBlockEnd: 0,
                      }}
                    >
                      {rateTrends.recent_examples.map((example, index) => (
                        <li
                          key={index}
                          style={{ marginBottom: 4, fontSize: '1rem', listStyleType: 'disc' }}
                        >
                          <Typography variant="body2">
                            {formatDateString(example.start_date)}
                            {example.start_date !== example.end_date
                              ? ` – ${formatDateString(example.end_date)}`
                              : ''}{' '}
                            ({getWeekdayFromDate(example.start_date)}
                            {example.start_date !== example.end_date
                              ? ` - ${getWeekdayFromDate(example.end_date)}`
                              : ''}
                            ) → {exchangeRates[currency].symbol}
                            <span style={customTextStyleScaleOnly}>
                              {formatCurrency(String(convertCurrency(currency, example.rate)))}
                            </span>
                            /night (observed {example.captured_days_ago} days prior)
                          </Typography>
                        </li>
                      ))}
                    </ul>
                    {!showScaleOnlyData && (
                      <Typography
                        style={{
                          ...styles.revealButton,
                          textDecoration: 'underline',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                        onClick={() => {
                          setMarketUrlBack();
                          triggerModalClose();
                          handleOnSampleClick('scale');
                        }}
                      >
                        {SCALE_CTA}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        ),
        placeholder: typingPlaceholder({
          marginBottom: '24px',
          message: 'Exploring rate trends...',
        }),
        typingDuration: 2000,
      },
      {
        key: 'occupancy-trends',
        content: (
          <Box sx={{ marginBottom: '24px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '8px' }}>
              Occupancy Trends
            </Typography>

            <Typography variant="body2" sx={{ marginBottom: '12px' }}>
              Occupancy (≈ <span style={customTextStyleScaleOnly}>{occupancyRate}%</span>) is based
              on month-by-month observations:
            </Typography>

            {occupancyTrends?.monthly_observations &&
              occupancyTrends.monthly_observations.length > 0 && (
                <Box sx={{ marginLeft: '16px', position: 'relative' }}>
                  <Box sx={showScaleOnlyData ? {} : { ...customTextStyleScaleOnly, pointerEvents: 'none' }}>
                    <ul style={{ paddingLeft: 20, marginBlockStart: 0, marginBlockEnd: 0 }}>
                      {occupancyTrends.monthly_observations.map((observation, index) => (
                        <li
                          key={index}
                          style={{ marginBottom: 12, fontSize: '1rem', listStyleType: 'disc' }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 2 }}
                          >
                            {formatMonthString(observation.month)}:{' '}
                            {observation.raw_occupancy_rate}–{observation.adjusted_occupancy_rate}%
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                            (
                            {observation.confirmed_booked_days_count} confirmed{' '}
                            {observation.confirmed_booked_days_count === 1 ? 'night' : 'nights'} booked
                            + {observation.last_minute_booked_days_count} last-minute{' '}
                            {observation.last_minute_booked_days_count === 1 ? 'night' : 'nights'}{' '}
                            assumed. Booked days: {observation.booked_days_count ?? '-'}, Active days:{' '}
                            {observation.active_days_count ?? '-'})
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                  {!showScaleOnlyData && (
                    <Typography
                      style={{
                        ...styles.revealButton,
                        textDecoration: 'underline',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                      onClick={() => {
                        setMarketUrlBack();
                        triggerModalClose();
                        handleOnSampleClick('scale');
                      }}
                    >
                      {SCALE_CTA}
                    </Typography>
                  )}
                </Box>
              )}
          </Box>
        ),
        placeholder: typingPlaceholder({
          marginBottom: '24px',
          message: 'Reviewing occupancy trends...',
        }),
        typingDuration: 1800,
      },
      {
        key: 'calendar-view',
        content: (
          <Box sx={{ marginBottom: '24px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '8px' }}>
              Calendar View
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: '8px' }}>
              Below is a combined snapshot of the {formatMonthString(endMonth)} calendar, collected
              across {latestCalendarView?.number_of_observation_points || 0} observation points,
              showing confirmed bookings only (no last-minute bookings are included).
            </Typography>
            <Box
              sx={{
                padding: '16px',
                background: '#f5f5f5',
                borderRadius: '8px',
              }}
            >
              {latestCalendarView?.combined_calendar_snapshot ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  sx={{
                    gap: 4,
                    mt: 2,
                  }}
                >
                  <Grid
                    item
                    key={`${endMonth.split('-')[0]}-${endMonth.split('-')[1]}`}
                    sx={{
                      ...customTextStyleScaleOnly,
                      pointerEvents: isUserAllowed ? 'auto' : 'none',
                    }}
                  >
                    <CalendarHeatmap
                      year={Number(endMonth.split('-')[0])}
                      month={Number(endMonth.split('-')[1]) - 1}
                      occupancyData={Object.entries(
                        latestCalendarView?.combined_calendar_snapshot
                      ).reduce((acc, [date, status]) => {
                        acc[date] = status === 'booked' ? 100 : 10;
                        return acc;
                      }, {} as Record<string, number>)}
                      monthTitle={formatMonthString(endMonth)}
                      showStatus={true}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2">No calendar snapshot available</Typography>
              )}
            </Box>
          </Box>
        ),
        placeholder: typingPlaceholder({
          marginBottom: '24px',
          message: 'Preparing calendar snapshot...',
        }),
        typingDuration: 1600,
      },
      {
        key: 'mid-term',
        content: (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '8px' }}>
              Mid-term / Unavailable Months
            </Typography>
            {midTermUnavailableMonths && midTermUnavailableMonths.length > 0 ? (
              <Box sx={{ marginLeft: '16px' }}>
                <ul style={{ paddingLeft: 20, marginBlockStart: 0, marginBlockEnd: 0 }}>
                  {midTermUnavailableMonths.map((month, index) => (
                    <li
                      key={index}
                      style={{ marginBottom: 4, fontSize: '1rem', listStyleType: 'disc' }}
                    >
                      <Typography variant="body2">
                        <span style={customTextStyleScaleOnly}>
                          {formatMonthString(month.month)}:
                        </span>{' '}
                        100% of nights were blocked, either booked mid-term or unavailable (
                        {month.blocked_days_count} {month.blocked_days_count > 1 ? 'days' : 'day'}{' '}
                        blocked).
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            ) : (
              <Typography variant="body2">No mid-term or unavailable months detected.</Typography>
            )}
          </Box>
        ),
        placeholder: typingPlaceholder({
          marginBottom: 0,
          message: 'Scanning for blocked periods...',
        }),
        typingDuration: 1200,
      },
    ],
    [
      annualRevenue,
      averageDailyRate,
      bookedNights,
      confirmedBookedNights,
      currency,
      customTextStyleScaleOnly,
      dataConfidenceSummary?.confidence_level,
      dataConfidenceSummary?.checks,
      endMonth,
      handleOnSampleClick,
      isDemo,
      isUserAllowed,
      midTermUnavailableMonths,
      occupancyRate,
      occupancyTrends?.monthly_observations,
      rawOccupancyRate,
      rateTrends?.observation_count_breakdown?.weekdays,
      rateTrends?.observation_count_breakdown?.weekends,
      rateTrends?.observation_count_last_3_months,
      rateTrends?.recent_examples,
      setMarketUrlBack,
      showScaleOnlyData,
      startMonth,
      totalNights,
      triggerModalClose,
      useRangeFormat,
    ]
  );

  useEffect(() => {
    if (!data || blocks.length === 0) {
      setRevealedBlocks(0);
      setActiveBlockIndex(null);
      animationCompletedRef.current = false;
      dataRef.current = null;
      return;
    }

    // Create a stable identifier from the data to track if we've animated it
    const dataIdentifier = JSON.stringify({
      revenue: data?.performance_summary?.revenue_potential,
      occupancy: data?.performance_summary?.occupancy_rate_ltm,
      adr: data?.performance_summary?.average_daily_rate_ltm,
    });

    // Check if data has changed
    const dataChanged = dataRef.current !== dataIdentifier;
    if (dataChanged) {
      dataRef.current = dataIdentifier;
      animationCompletedRef.current = false;
    }

    // If animation already completed for this data, show all blocks immediately
    if (animationCompletedRef.current) {
      setRevealedBlocks(blocks.length);
      setActiveBlockIndex(null);
      return;
    }

    let isCancelled = false;

    const runAnimation = async () => {
      setRevealedBlocks(0);

      for (let i = 0; i < blocks.length; i += 1) {
        if (isCancelled) {
          break;
        }

        setActiveBlockIndex(i);
        const typingDuration = blocks[i].typingDuration ?? defaultTypingDuration;
        await wait(typingDuration);
        if (isCancelled) {
          break;
        }
        setRevealedBlocks(prev => prev + 1);
        await wait(blockPauseDuration);
      }

      if (!isCancelled) {
        setActiveBlockIndex(null);
        animationCompletedRef.current = true;
      }
    };

    runAnimation();

    return () => {
      isCancelled = true;
    };
  }, [blocks, data]);

  return (
    <Box sx={{ padding: '20px', pt: 0 }}>
      {blocks.map((block, index) => {
        const isVisible = index < revealedBlocks;
        const isTyping = activeBlockIndex === index;

        if (!isVisible && !isTyping) {
          return null;
        }

        return (
          <Box key={block.key} sx={{ width: '100%' }}>
            {isVisible ? (
              <Box
                sx={{
                  width: '100%',
                  animation: 'fadeIn 320ms ease-out',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(4px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                {block.content}
              </Box>
            ) : (
              block.placeholder
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const PerformanceExplanationsSection: React.FC<PerformanceExplanationsSectionProps> = ({
  listingID,
  handleOnSampleClick,
  currency,
  setMarketUrlBack,
  triggerModalClose,
  isDemo,
  isDisabled,
  marketID,
}) => {
  const user: User = useSelector((state: any) => state.dashboard.user);
  const isUserAllowed =
    user.subscriptionPlan === 'scale' || String(listingID) === SAMPLE_LISTING_ID;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ExplanationData | null>(null);
  const [isApiRateLimited, setIsApiRateLimited] = useState(false);

  const getListingPerformanceExplanation = async (listingId: string) => {
    setLoading(true);
    const URL = 'https://8q15spida4.execute-api.us-east-2.amazonaws.com/default/getListingDetails';
    const config = {
      queryParams: {
        listingID: listingId,
        requestType: 'explanation',
        apiType: 'v5',
        marketID: marketID ? marketID : ''
      },
    };
    const resp = await callApi(URL, config, true, false, '', 'GET', true, undefined, true);
    if (resp.error) {
      setLoading(false);
      return;
    }
    if (resp.status === 429) {
      setLoading(false);
      setIsApiRateLimited(true);
    } else {
      setData((resp?.message as ExplanationData) || null);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (listingID) {
      getListingPerformanceExplanation(listingID);
    }
  }, [listingID]);

  return (
    <Grid container sx={{ width: '100%' }}>
      {loading ? (
        <Box sx={{ padding: '20px', pt: 0 }}>
          <TypingLoader size={12} color="#000" />
        </Box>
      ) : isApiRateLimited ? (
        <Box sx={{ width: '100%', height: '80px' }}>
          <RateLimitAlert handleOnSampleClick={handleOnSampleClick} />
        </Box>
      ) : (
        <Template
          premiumData={data}
          currency={currency}
          isUserAllowed={isUserAllowed}
          isDemo={isDemo}
          setMarketUrlBack={setMarketUrlBack}
          triggerModalClose={triggerModalClose}
          handleOnSampleClick={handleOnSampleClick}
          isLtmDataDisabled={isDisabled}
        />
      )}
    </Grid>
  );
};

export default PerformanceExplanationsSection;
